<script setup>
import { ref, watch, provide } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { complaintChannels } from '@/services/supabase'

const route = useRoute()
const channel = ref(null)
const loading = ref(true)
const error = ref(null)

// Prover dados do canal para os componentes filhos
provide('channel', channel)

watch(
  () => route.params.slug,
  async (slug) => {
    if (!slug) {
      error.value = 'Canal não encontrado'
      loading.value = false
      return
    }

    loading.value = true
    error.value = null
    channel.value = null

    try {
      const { data, error: fetchError } = await complaintChannels.getBySlug(slug)

      if (fetchError || !data) {
        error.value = 'Canal não encontrado ou inativo'
      } else {
        channel.value = data
      }
    } catch (e) {
      console.error('Error fetching channel:', e)
      error.value = 'Erro ao carregar canal'
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)
</script>

<template>
  <div 
    class="min-h-screen"
    :style="{ 
      '--primary-color': channel?.primary_color || '#1e40af',
      '--secondary-color': channel?.secondary_color || '#3b82f6'
    }"
  >
    <!-- Loading state -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div class="spinner w-12 h-12 mx-auto mb-4"></div>
        <p class="text-gray-600">Carregando...</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center max-w-md mx-auto px-4">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 class="text-xl font-bold text-gray-900 mb-2">{{ error }}</h1>
        <p class="text-gray-600">O canal de denúncias solicitado não está disponível.</p>
      </div>
    </div>

    <!-- Channel content -->
    <div v-else class="min-h-screen flex flex-col bg-gray-50">
      <!-- Header -->
      <header 
        class="py-6 px-4"
        :style="{ backgroundColor: channel?.primary_color || '#1e40af' }"
      >
        <div class="max-w-2xl mx-auto text-center">
          <!-- Company Logo -->
          <div v-if="channel?.company_logo_url" class="mb-4">
            <img 
              :src="channel.company_logo_url" 
              :alt="channel.company_name"
              class="h-16 mx-auto object-contain bg-white rounded-lg p-2"
            />
          </div>
          
          <h1 class="text-2xl md:text-3xl font-bold text-white">
            Canal de Denúncias
          </h1>
          <p class="text-white/90 mt-2">
            {{ channel?.company_name }}
          </p>
        </div>
      </header>

      <!-- Main content -->
      <main class="flex-1 py-8 px-4">
        <div class="max-w-2xl mx-auto">
          <RouterView />
        </div>
      </main>

      <!-- Footer -->
      <footer class="py-6 px-4 text-center text-sm text-gray-500 border-t border-gray-200">
        <p>Este canal é operado de forma independente e segura.</p>
        <p class="mt-1">
          Powered by <a href="/" class="text-primary-600 hover:underline">Ergonlider</a>
        </p>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.spinner {
  border: 4px solid #e5e7eb;
  border-top-color: var(--primary-color, #1e40af);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
